/**
 * HubSpot live chat is CRM / human backup only.
 * The site AI agent is the primary visitor chat; HubSpot loads on demand.
 */

type HubSpotConversationsWidget = {
  load?: (options?: { widgetOpen?: boolean }) => void;
  open?: () => void;
  close?: () => void;
  remove?: () => void;
  refresh?: (options?: { openToNewThread?: boolean }) => void;
  status?: () => { loaded?: boolean };
};

type HubSpotConversationsApi = {
  widget?: HubSpotConversationsWidget;
};

declare global {
  interface Window {
    hsConversationsSettings?: {
      loadImmediately?: boolean;
      identificationEmail?: string;
      identificationToken?: string;
      [key: string]: unknown;
    };
    hsConversationsOnReady?: Array<() => void>;
    HubSpotConversations?: HubSpotConversationsApi;
  }
}

const HIDDEN_ATTR = 'data-hubspot-chat';
let humanChatRequested = false;
let onReadyBound = false;
let openPollTimer: ReturnType<typeof setInterval> | null = null;

function setHiddenAttr(hidden: boolean) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute(HIDDEN_ATTR, hidden ? 'hidden' : 'visible');
}

/** Call as early as possible so HubSpot does not auto-show its launcher. */
export function deferHubSpotChatLoad() {
  if (typeof window === 'undefined') return;
  window.hsConversationsSettings = {
    ...(window.hsConversationsSettings || {}),
    loadImmediately: false,
  };
  setHiddenAttr(true);
}

function getWidget(): HubSpotConversationsWidget | null {
  return window.HubSpotConversations?.widget || null;
}

function removeWidgetIfPresent() {
  const widget = getWidget();
  if (!widget) return;
  try {
    if (typeof widget.close === 'function') widget.close();
    if (typeof widget.remove === 'function') widget.remove();
  } catch (err) {
    console.warn('[hubspot] Failed to hide conversations widget:', err);
  }
}

function bindOnReadyHide() {
  if (typeof window === 'undefined' || onReadyBound) return;
  onReadyBound = true;
  const existing = window.hsConversationsOnReady || [];
  window.hsConversationsOnReady = [
    ...existing,
    () => {
      if (!humanChatRequested) {
        removeWidgetIfPresent();
        setHiddenAttr(true);
      }
    },
  ];
}

/** Keep HubSpot launcher hidden until the visitor asks for a human. */
export function keepHubSpotChatHidden() {
  if (typeof window === 'undefined') return;
  deferHubSpotChatLoad();
  bindOnReadyHide();
  if (!humanChatRequested) {
    removeWidgetIfPresent();
    setHiddenAttr(true);
  }
}

export function isHubSpotHumanChatOpen(): boolean {
  return humanChatRequested;
}

function clearOpenPoll() {
  if (openPollTimer) {
    clearInterval(openPollTimer);
    openPollTimer = null;
  }
}

/**
 * Load (if needed) then open the HubSpot widget.
 * Returns true when HubSpotConversations.widget was available and we invoked load/open.
 */
function revealHubSpotDom() {
  const el = document.getElementById('hubspot-messages-iframe-container');
  if (!el) return;
  el.style.setProperty('display', 'block', 'important');
  el.style.setProperty('visibility', 'visible', 'important');
  el.style.setProperty('pointer-events', 'auto', 'important');
}

function tryLoadAndOpenWidget(): boolean {
  const widget = getWidget();
  if (!widget) return false;

  try {
    // Always try load — safe when already loaded; required after remove() or loadImmediately:false
    if (typeof widget.load === 'function') {
      widget.load({ widgetOpen: true });
    }
    if (typeof widget.open === 'function') {
      widget.open();
    }
    revealHubSpotDom();

    // HubSpot often needs a beat after load before open sticks
    window.setTimeout(() => {
      try {
        getWidget()?.open?.();
        revealHubSpotDom();
      } catch {
        /* ignore */
      }
    }, 400);
    window.setTimeout(() => {
      try {
        getWidget()?.open?.();
        revealHubSpotDom();
      } catch {
        /* ignore */
      }
    }, 1200);

    return true;
  } catch (err) {
    console.warn('[hubspot] Failed to open conversations widget:', err);
    return false;
  }
}

function whenConversationsReady(callback: () => void) {
  if (typeof window === 'undefined') return;

  if (window.HubSpotConversations?.widget) {
    callback();
    return;
  }

  const existing = window.hsConversationsOnReady || [];
  window.hsConversationsOnReady = [
    ...existing,
    () => {
      callback();
    },
  ];
}

/**
 * Load and open HubSpot live chat for human / sales handoff.
 * Safe to call repeatedly. Polls briefly if the Conversations SDK is not ready yet.
 */
export function openHubSpotHumanChat(): boolean {
  if (typeof window === 'undefined') return false;

  humanChatRequested = true;
  setHiddenAttr(false);

  window.hsConversationsSettings = {
    ...(window.hsConversationsSettings || {}),
    loadImmediately: false,
  };

  const opened = tryLoadAndOpenWidget();
  if (opened) return true;

  whenConversationsReady(() => {
    tryLoadAndOpenWidget();
  });

  // Poll in case onReady already fired before we registered
  clearOpenPoll();
  const started = Date.now();
  openPollTimer = setInterval(() => {
    if (tryLoadAndOpenWidget() || Date.now() - started > 15000) {
      clearOpenPoll();
    }
  }, 300);

  return Boolean(window.HubSpotConversations?.widget);
}

/**
 * Used by visitor-identify: apply tokens without forcing the launcher visible
 * unless human chat was already requested.
 */
export function applyHubSpotIdentification(email: string, token: string) {
  if (typeof window === 'undefined') return;

  window.hsConversationsSettings = {
    ...(window.hsConversationsSettings || {}),
    loadImmediately: false,
    identificationEmail: email,
    identificationToken: token,
  };

  if (!humanChatRequested) {
    bindOnReadyHide();
    return;
  }

  whenConversationsReady(() => {
    try {
      const widget = getWidget();
      if (!widget) return;
      if (typeof widget.refresh === 'function') {
        widget.refresh();
        return;
      }
      tryLoadAndOpenWidget();
    } catch (err) {
      console.warn('[hubspot] Failed to refresh identified widget:', err);
    }
  });
}
